import { Col, Row, Text } from 'components';
import { Api } from 'lib';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Header } from 'screens/Admin/components';
import {
  CallsTable,
  EnvironmentNotice,
  FilterBar,
  HealthPanel,
  MetricsBar,
  Pagination,
  RunsTable
} from './components';
import { DEFAULT_FILTERS, PAGE_SIZE, TAB, buildExportUrl, toParams } from './components/helpers';

const tabBtn = (active) =>
  `h-[34px] px-4 rounded-lg text-[13px] font-medium transition-colors border ${
    active
      ? 'bg-[#334155] text-white border-[#334155]'
      : 'bg-white text-[#475569] border-[#D7DEE8] hover:bg-[#F1F5F9]'
  }`;

const AdminActivityLog = () => {
  const { t } = useTranslation();

  // Health is the landing view: the page is opened to ask 'is anything wrong right now', and
  // that question is answered by the summary, not by reading rows.
  const [tab, setTab] = useState(TAB.HEALTH);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [options, setOptions] = useState({});
  const [data, setData] = useState(null);
  const [health, setHealth] = useState(null);
  const [metrics, setMetrics] = useState(null);
  const [groupBy, setGroupBy] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    Api.get('/admin/activity/filters').then((res) => {
      if (!cancelled && res?.data) setOptions(res.data);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchRows = useCallback(async (f, p, limit, view) => {
    setLoading(true);
    const params = toParams(f, { page: p, limit });
    const res = await Api.get(`/admin/activity/${view}?${params.toString()}`);
    if (res?.data) {
      setData(res.data);
      setPage(p);
    }
    setLoading(false);
  }, []);

  const fetchHealth = useCallback(async () => {
    setLoading(true);
    const res = await Api.get('/admin/activity/health');
    if (res?.data) setHealth(res.data);
    setLoading(false);
  }, []);

  const fetchMetrics = useCallback(async (f, group) => {
    // Metrics needs an explicit period; default to the last 7 UTC days when none is picked.
    const to = f.to || new Date().toISOString().slice(0, 10);
    const from = f.from || new Date(Date.now() - 6 * 86_400_000).toISOString().slice(0, 10);
    const params = toParams({ ...f, from, to }, group ? { groupBy: group } : {});
    const res = await Api.get(`/admin/activity/metrics?${params.toString()}`);
    if (res?.data) setMetrics(res.data);
  }, []);

  useEffect(() => {
    if (tab === TAB.HEALTH) {
      fetchHealth();
      fetchMetrics(appliedFilters, groupBy);
      return;
    }
    fetchRows(appliedFilters, 1, pageSize, tab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, tab, groupBy]);

  const handleFilterChange = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const handleApply = () => setAppliedFilters(filters);

  const handleClear = () => {
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    fetchRows(appliedFilters, 1, size, tab);
  };

  /*
    The export is fetched from the server rather than built from the rows on screen.

    The backend endpoint applies the same allow-list projection and the same 10,000-row cap, and it
    writes the environment onto every row — which is what lets exports from several environments be
    combined into the platform-wide figure no single screen can show.
  */
  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await Api.get(
        buildExportUrl(tab === TAB.RUNS ? 'runs' : 'calls', appliedFilters)
      );
      const csv = typeof res === 'string' ? res : res?.data;
      if (csv) {
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `activity-${tab}-${data?.environment || 'export'}-${Date.now()}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } finally {
      setExporting(false);
    }
  };

  /*
    Switching tabs resets the filters rather than carrying them across.

    The two views share only a time range: an outcome of "failure" means something on a call and
    nothing on a run, and a job filter is meaningless on calls. Carrying a stale filter over would
    show an empty table that reads as "no activity".
  */
  const handleTabChange = (next) => {
    if (next === tab) return;
    setFilters(DEFAULT_FILTERS);
    setAppliedFilters(DEFAULT_FILTERS);
    setTab(next);
  };

  /** Jump from a run to the provider calls it made — the run-to-calls half of an incident trace. */
  const handleTraceCalls = (runId) => {
    const next = { ...DEFAULT_FILTERS, runId };
    setFilters(next);
    setAppliedFilters(next);
    setTab(TAB.CALLS);
  };

  const handleTraceSubject = (subjectId) => {
    const next = { ...DEFAULT_FILTERS, subjectId };
    setFilters(next);
    setAppliedFilters(next);
    setTab(TAB.CALLS);
  };

  return (
    <>
      <Header>
        <Row center between wid="100%">
          <Text h3>{t('admin.activityLog.title', { defaultValue: 'Activity Log' })}</Text>
        </Row>
      </Header>

      <Col start noFlex wid="100%" className="mt-[54px] h-[calc(100vh-54px)] overflow-y-auto">
        <div className="p-6 max-w-[1400px] mx-auto w-full">
          <EnvironmentNotice
            environment={health?.environment || data?.environment || options?.environment}
            environmentRaw={health?.environmentRaw ?? options?.environmentRaw}
            allowanceShared={options?.allowanceShared}
          />

          <div className="flex gap-2 mb-4">
            <button
              type="button"
              className={tabBtn(tab === TAB.HEALTH)}
              onClick={() => handleTabChange(TAB.HEALTH)}
            >
              {t('admin.activityLog.tabs.health', { defaultValue: 'Overview' })}
            </button>
            <button
              type="button"
              className={tabBtn(tab === TAB.CALLS)}
              onClick={() => handleTabChange(TAB.CALLS)}
            >
              {t('admin.activityLog.tabs.calls', { defaultValue: 'Provider calls' })}
            </button>
            <button
              type="button"
              className={tabBtn(tab === TAB.RUNS)}
              onClick={() => handleTabChange(TAB.RUNS)}
            >
              {t('admin.activityLog.tabs.runs', { defaultValue: 'Scheduled jobs' })}
            </button>
          </div>

          {tab === TAB.HEALTH && (
            <>
              <HealthPanel health={health} loading={loading} />
              <MetricsBar
                metrics={metrics}
                loading={loading}
                groupBy={groupBy}
                onGroupByChange={setGroupBy}
              />
            </>
          )}

          <FilterBar
            tab={tab}
            filters={filters}
            options={options}
            onChange={handleFilterChange}
            onApply={handleApply}
            onClear={handleClear}
            onExport={handleExport}
            exporting={exporting}
          />

          {tab !== TAB.HEALTH && (
            <>
              {tab === TAB.RUNS ? (
                <RunsTable rows={data?.rows} loading={loading} onTraceCalls={handleTraceCalls} />
              ) : (
                <CallsTable
                  rows={data?.rows}
                  loading={loading}
                  onTraceSubject={handleTraceSubject}
                />
              )}

              <Pagination
                page={page}
                total={data?.total || 0}
                pageSize={pageSize}
                onPageSizeChange={handlePageSizeChange}
                onGoToPage={(p) => fetchRows(appliedFilters, p, pageSize, tab)}
              />
            </>
          )}
        </div>
      </Col>
    </>
  );
};

export default AdminActivityLog;
